import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
  TextField
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

function Profile() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    displayName: currentUser?.displayName || '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
    address: currentUser?.address || ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      setSnackbar({
        open: true,
        message: 'Başarıyla çıkış yapıldı',
        severity: 'success'
      });
      navigate('/');
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error);
      setSnackbar({
        open: true,
        message: 'Çıkış yapılırken bir hata oluştu',
        severity: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: formData.displayName,
        phone: formData.phone,
        address: formData.address,
        updatedAt: new Date()
      });
      setSuccess('Profil bilgileriniz güncellendi');
    } catch (error) {
      setError('Profil güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <>
      <Container maxWidth="md" sx={{ py: 4, mt: { xs: '56px', sm: '64px' } }}>
        <Paper elevation={0} sx={{ p: 4, bgcolor: '#f8f8f8', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom>
            Profilim
          </Typography>
          
          <Divider sx={{ my: 3 }} />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              {/* Kullanıcı Bilgileri */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Hesap Bilgileri
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="E-posta" 
                      secondary={currentUser.email} 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Hesap Oluşturma Tarihi" 
                      secondary={new Date(currentUser.metadata.creationTime).toLocaleDateString('tr-TR')} 
                    />
                  </ListItem>
                </List>
              </Grid>

              {/* Hesap İşlemleri */}
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>
                  Hesap İşlemleri
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/my-orders')}
                  >
                    Siparişlerim
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate('/favorites')}
                  >
                    Favorilerim
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleLogout}
                  >
                    Çıkış Yap
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Profil Bilgilerim
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ad Soyad"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="E-posta"
                  value={formData.email}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adres"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? 'Güncelleniyor...' : 'Güncelle'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
      
      {/* Snackbar ekliyoruz */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Profile; 